import { useCallback, useEffect } from "react";
import { createSearchParams, useNavigate } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import LanguageSelectForm from "components/molecules/LanguageSelectForm";
import { STORAGE_LANGUAGE_NAME } from "constants/variables";
import { useGTM } from "hooks/useGTM";
import { useBreakpoint } from "hooks/useBreakpoint";
import { useLanguage } from "hooks/useLanguage";
import { getLocalStorageItem, setLocalStorageItem } from "utils/localStorage";
import { makeLangUrl } from "helpers/makeLangUrl";
import { GET_PARAMS, GET_POPUP_TYPES } from "constants/popupConfig";
import { useCatalogsStore } from "stores/catalogs";
import { LocalizationType } from "types/Catalogs";
import { fetchLocalization } from "features/localization/fetchLocalization";
import { useSettingsStore } from "stores/settings";

const LanguageSelectFormContainer = () => {
  const GTM = useGTM();
  const languages = useCatalogsStore((state) => state.languages);
  const setLocalization = useCatalogsStore((state) => state.setLocalization);
  const { language, setServiceError, changeLanguage } = useSettingsStore(
    (state) => ({
      setServiceError: state.setServiceError,
      changeLanguage: state.changeLanguage,
      language: state.language,
    }),
  );
  const selectedLanguage =
    language || getLocalStorageItem(STORAGE_LANGUAGE_NAME);
  const { isDesktop } = useBreakpoint();
  const navigate = useNavigate();
  const { path } = useLanguage();
  const methods = useForm();
  const requestLocalization = useCallback(
    async (name: string): Promise<LocalizationType[] | undefined> => {
      try {
        const response = await fetchLocalization(name);
        if (response) {
          setLocalization(response);
        }
        return response;
      } catch {
        setServiceError(true);
        return undefined;
      }
    },
    [setServiceError, setLocalization],
  );

  useEffect(() => {
    if (!selectedLanguage) {
      GTM.changeLanguage(selectedLanguage);
    }
  }, [selectedLanguage, GTM]);

  const handleLanguageChange = (name: string) => {
    GTM.clickChangeLanguage(name);
    const storage = getLocalStorageItem(STORAGE_LANGUAGE_NAME);

    if (name !== storage) {
      requestLocalization(name);
      changeLanguage(name);
      setLocalStorageItem(STORAGE_LANGUAGE_NAME, name.toUpperCase());
    }

    navigate(
      {
        pathname: makeLangUrl(path),
        ...(!isDesktop && {
          search: `?${createSearchParams({ [GET_PARAMS.POPUP]: GET_POPUP_TYPES.popup.sideMenu })}`,
        }),
      },
      { replace: true },
    );
  };

  if (!languages) {
    return null;
  }

  return (
    <FormProvider {...methods}>
      <LanguageSelectForm
        languages={languages}
        selectedLanguage={selectedLanguage}
        onChange={handleLanguageChange}
      />
    </FormProvider>
  );
};

export default LanguageSelectFormContainer;
